import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { BsFillPencilFill } from "react-icons/bs";
import { FaCheck, FaTrashAlt } from "react-icons/fa";

import Api from "../../../config/Api";
import Table from "../../Components/Widget/Table";
import Loader from "../../Components/Widget/Loader";
import TooltipWidget from "../../Components/Widget/TooltipWidget";
import StatusBadge from "../Application/Components/StatusBadge";

const API = {
  list: "failed_payment_applications",
  crossCheck: "failed_payment_applications/cross-check",
  hardDeleteSubmit: "failed_payment_applications/submit-hard-delete",
};

const FailedPaymentApplications = () => {
  const navigate = useNavigate();
  const admin = useSelector((state) => JSON.parse(state.authentication.admin));
  const permissions = admin?.role?.permission || [];
  const isHod = permissions.includes("HOD");
  const isReliefOfficer = permissions.includes("Relief Officer");

  const [isLoading, setIsLoading] = useState(false);
  const [failedPaymentApplications, setFailedPaymentApplications] = useState(
    []
  );
  const [isApplicationLoaded, setIsApplicationLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [filters, setFilters] = useState({
    application_id: "",
  });

  const getMapping = (row) =>
    row?.mapping || row?.latest_mapping || row?.current_mapping || null;

  const getOldApplicationId = (row) =>
    row?.old_application_id ||
    row?.application_id ||
    row?.failed_application_id ||
    row?.old_application?.application_id ||
    "-";

  const getFailedApplicationEditId = (row) =>
    row?.old_application_table_id || row?.old_application?.id || row?.id || "";

  const getBeneficiaryName = (row) =>
    row?.beneficiary_name ||
    row?.old_applicant_name ||
    row?.applicant_name ||
    row?.old_application?.applicant_name ||
    row?.application?.applicant_name ||
    "-";

  const getApplicantMobile = (row) =>
    row?.mobile ||
    row?.old_application_mobile ||
    row?.old_applicant_mobile ||
    row?.old_application?.mobile ||
    row?.application?.mobile ||
    "-";

  const getApplicantPhoto = (row) =>
    row?.applicant_default_img ||
    row?.old_application?.applicant_default_img ||
    row?.application?.applicant_default_img ||
    "";

  const getDistrict = (row) =>
    row?.district?.district_name ||
    row?.district_name ||
    row?.old_application?.district?.district_name ||
    row?.application?.district?.district_name ||
    "-";

  const getCurrentStatus = (row) =>
    row?.current_application_status ||
    row?.application_status ||
    row?.status ||
    row?.old_application?.status ||
    row?.application?.status ||
    "";

  const buildListUrl = (page = 1, pageSize = perPage, appliedFilters = filters) => {
    const params = new URLSearchParams();
    params.append("page_no", page);
    params.append("per_page", pageSize);

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    return `${API.list}?${params.toString()}`;
  };

  const getFailedPaymentApplications = async (
    page = 1,
    pageSize = perPage,
    appliedFilters = filters
  ) => {
    try {
      setIsLoading(true);
      const res = await Api(buildListUrl(page, pageSize, appliedFilters), "GET");
      const responseData = res?.data || {};
      const payload =
        responseData?.status === 1 &&
        responseData?.data &&
        typeof responseData.data === "object" &&
        !Array.isArray(responseData.data)
          ? responseData.data
          : responseData;
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
        ? responseData
        : [];
      const isWrappedSuccess = responseData?.status === 1;
      const isDirectPaginatedSuccess = list.length > 0 || Array.isArray(payload?.data);

      if (isWrappedSuccess || isDirectPaginatedSuccess) {
        const pagination =
          payload?.pagination && typeof payload.pagination === "object"
            ? payload.pagination
            : payload &&
              typeof payload === "object" &&
              payload.current_page
            ? {
                page_no: payload.current_page,
                per_page: payload.per_page,
                total_records: payload.total,
              }
            : null;

        setFailedPaymentApplications(list);
        setCurrentPage(pagination?.page_no || page);
        setPerPage(pagination?.per_page || pageSize);
        setTotalRecords(
          pagination?.total_records ||
            payload?.total ||
            payload?.count ||
            list.length
        );
        setIsApplicationLoaded(true);
      } else {
        toast.error(responseData?.message || "Failed to load applications", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setIsApplicationLoaded(true);
      }
    } catch (error) {
      toast.error(error?.data?.message || "Something went wrong!", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      setIsApplicationLoaded(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setResetPaginationToggle((prev) => !prev);
    getFailedPaymentApplications(1, perPage, filters);
  };

  const handleReset = () => {
    const initialFilters = {
      application_id: "",
    };

    setFilters(initialFilters);
    setCurrentPage(1);
    setResetPaginationToggle((prev) => !prev);
    getFailedPaymentApplications(1, perPage, initialFilters);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getFailedPaymentApplications(page, perPage, filters);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    setResetPaginationToggle((prev) => !prev);
    getFailedPaymentApplications(1, newPerPage, filters);
  };

  const markAsCrossChecked = async (row) => {
    try {
      setIsLoading(true);
      const res = await Api(API.crossCheck, "POST", {
        old_application_id: getOldApplicationId(row),
      });

      if (res?.data?.status === 1) {
        toast.success(res?.data?.message || "Application marked as cross-checked.", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        getFailedPaymentApplications(currentPage, perPage, filters);
      } else {
        toast.error(res?.data?.message || "Unable to mark as cross-checked.", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    } catch (error) {
      toast.error(error?.data?.message || "Unable to mark as cross-checked.", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitForHardDelete = async (row) => {
    if (
      window.confirm(
        "Are you sure you want to submit this Application ID for hard deletion/data correction?"
      ) !== true
    ) {
      return;
    }

    try {
      setIsLoading(true);
      const res = await Api(API.hardDeleteSubmit, "POST", {
        old_application_id: getOldApplicationId(row),
      });

      if (res?.data?.status === 1) {
        toast.success(
          res?.data?.message ||
            "Application submitted for hard deletion/data correction.",
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
        getFailedPaymentApplications(currentPage, perPage, filters);
      } else {
        toast.error(
          res?.data?.message ||
            "Unable to submit for hard deletion/data correction.",
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    } catch (error) {
      toast.error(
        error?.data?.message ||
          "Unable to submit for hard deletion/data correction.",
        {
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const openFailedApplicationEdit = (row) => {
    const editId = getFailedApplicationEditId(row);

    if (!editId) {
      toast.error("Failed application edit record was not found.", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    navigate("/failed-sdrf-1", {
      state: {
        id: editId,
        isEdit: true,
        isFailedPaymentEdit: true,
        oldApplicationId: getOldApplicationId(row),
      },
    });
  };

  useEffect(() => {
    getFailedPaymentApplications(1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only role 8 may act; other roles (e.g. role 3) are view-only even if they hold HOD/Relief Officer permissions.
  const canManage = Number(admin?.role?.id) === 8 && (isHod || isReliefOfficer);

  const columns = [
      {
        name: "#",
        cell: (_row, index) => (currentPage - 1) * perPage + index + 1,
        width: "70px",
      },
      {
        name: "Application ID",
        selector: (row) => getOldApplicationId(row),
        sortable: true,
        wrap: true,
        minWidth: "170px",
      },
      {
        name: "Applicant Passport Photo",
        selector: (row) =>
          getApplicantPhoto(row) ? (
            <img
              src={getApplicantPhoto(row)}
              alt="Applicant"
              style={{
                height: "70px",
                width: "100px",
                objectFit: "cover",
              }}
            />
          ) : (
            "-"
          ),
      },
      {
        name: "Applicant Info",
        selector: (row) => (
          <>
            <div className="mb-1">{getBeneficiaryName(row)}</div>
            <div>{getApplicantMobile(row)}</div>
          </>
        ),
        minWidth: "180px",
      },
      {
        name: "District",
        selector: (row) => getDistrict(row),
        sortable: true,
        wrap: true,
      },
      {
        name: "Status",
        selector: (row) =>
          getCurrentStatus(row) ? <StatusBadge status={getCurrentStatus(row)} /> : "-",
        sortable: true,
        wrap: true,
      },
      ...(canManage
        ? [
            {
              name: "Action",
              selector: (row) => {
                const mapping = getMapping(row);

                return (
                  <div className="d-flex flex-wrap gap-1">
                    {isHod ? (
                      <TooltipWidget title="Edit Failed Application">
                        <button
                          className={`btn btn-sm ${
                            mapping?.id ? "btn-primary" : "btn-success"
                          }`}
                          type="button"
                          onClick={() => openFailedApplicationEdit(row)}
                        >
                          {mapping?.id ? <BsFillPencilFill /> : <FaCheck />}
                        </button>
                      </TooltipWidget>
                    ) : null}

                    {isReliefOfficer ? (
                      <TooltipWidget title="Mark as Cross-Checked">
                        <button
                          className="btn btn-success btn-sm"
                          type="button"
                          onClick={() => markAsCrossChecked(row)}
                          disabled={Boolean(row?.cross_checked_at || row?.cross_checked_by)}
                        >
                          <FaCheck />
                        </button>
                      </TooltipWidget>
                    ) : null}

                    {isReliefOfficer ? (
                      <TooltipWidget title="Submit for Hard Deletion/Data Correction">
                        <button
                          className="btn btn-danger btn-sm"
                          type="button"
                          onClick={() => submitForHardDelete(row)}
                        >
                          <FaTrashAlt />
                        </button>
                      </TooltipWidget>
                    ) : null}
                  </div>
                );
              },
              minWidth: "220px",
            },
          ]
        : []),
    ];

  return (
    <>
      <div className="bg-white p-3 rounded shadow">
        <div className="mb-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-lg-3">
              <label className="form-label">Application ID</label>
              <input
                className="form-control"
                name="application_id"
                onChange={handleFilterChange}
                value={filters.application_id}
              />
            </div>
            <div className="col-12 col-lg-auto d-flex gap-2 align-items-end">
              <button
                className="btn btn-primary"
                type="button"
                onClick={handleSearch}
              >
                Search
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="mb-2 d-flex justify-content-between align-items-center">
          <h5 className="text-dark mb-0">Failed Payment Applications</h5>
        </div>

        <div>
          {isApplicationLoaded ? (
            <Table
              columns={columns}
              data={failedPaymentApplications}
              paginationServer
              progressPending={isLoading}
              paginationTotalRows={totalRecords}
              paginationDefaultPage={currentPage}
              paginationPerPage={perPage}
              paginationResetDefaultPage={resetPaginationToggle}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              noDataComponent={
                <div className="py-4 text-muted">
                  No failed payment applications found.
                </div>
              }
            />
          ) : (
            <Loader />
          )}
        </div>
      </div>
    </>
  );
};

export default FailedPaymentApplications;
